import React, { Component } from "react";
import { i18n } from "../services/i18nService";
import { config } from "../config/config";
import { getSdk } from "../services/sdkService";
import { getLogger } from "../utils/logger";
import { routes } from "../routes";
import { isForceLogout } from "../utils/isForceLogout";

let applicationState = {
  userLogged: false,
  userLocale: null,
  parentalLevel: null,
  focusedCarouselIndex: 0,
  focusedCarouselItemIndex: 0,
  focusedMainMenuItemIndex: 0,
  focusedMediaControlItemIndex: -1,
  focusedFilterItemIndex: 0,
  layoutScrollTop: 0,
  theme: null,
  userAccountStatus: null,
  currentFocus: null,
  buildInfo: null,
  isOnline: true,
  appStarted: null,
  supports4K: false,
  userMediaCached: null,
  voucherData: null,
  userRelatedModules: null,
  addons: [],
  signupUsername: "",
  subscriptions: null,
  sCouponCode: null,
  userBilling: null,
  showPromotionPage: true,
  menuExpanded: false,
  lastFocusedName: "lists",
  paymentMethods: [],
  posterLandscapeImageForBuyAsset: "",
  userSwitchPlaninfo: null
};

const ApplicationState = React.createContext(applicationState);

/**
 * Provider component that uses the React Context API to make the global app state available for the child components
 * interested on reading or manipulating the state. This component should wrap all the components that need the state so
 * it should be one of the higher components on the application's component tree.
 */
export class StateProvider extends Component {
  constructor(props) {
    super(props);

    applicationState.userLocale = i18n.getLocale() || applicationState.userLocale || config.get().sdk.defaultLocale;
    applicationState.parentalLevel = getSdk().parentalLevel;

    this.userLoggedIn = this.userLoggedIn.bind(this);
    this.userLoggedOut = this.userLoggedOut.bind(this);
    this.setUserAccountStatus = this.setUserAccountStatus.bind(this);
    this.setAddons = this.setAddons.bind(this);
    this.setPlayer = this.setPlayer.bind(this);
    this.setPlayerConfig = this.setPlayerConfig.bind(this);
    this.setLocale = this.setLocale.bind(this);
    this.setSubscriptions = this.setSubscriptions.bind(this);
    this.setParentalLevel = this.setParentalLevel.bind(this);
    this.showGlobalModal = this.showGlobalModal.bind(this);
    this.hideGlobalModal = this.hideGlobalModal.bind(this);
    this.showSettingsModal = this.showSettingsModal.bind(this);
    this.hideSettingsModal = this.hideSettingsModal.bind(this);
    this.changeTheme = this.changeTheme.bind(this);
    this.setCurrentFocus = this.setCurrentFocus.bind(this);
    this.setAppState = this.setAppState.bind(this);
    this.setMenuItemFocus = this.setMenuItemFocus.bind(this);
    this.setMediaControlItemFocus = this.setMediaControlItemFocus.bind(this);
    this.setCarouselItemFocus = this.setCarouselItemFocus.bind(this);
    this.setCarouselFocus = this.setCarouselFocus.bind(this);
    this.setPosterLandscapeImage = this.setPosterLandscapeImage.bind(this);
    this.applyKidsTheme = this.applyKidsTheme.bind(this);
    this.setAppStartedValue = this.setAppStartedValue.bind(this);
    this.setShowPromotionPageValue = this.setShowPromotionPageValue.bind(this);
    this.setLayoutScrollTop = this.setLayoutScrollTop.bind(this);
    this.setUserBilling = this.setUserBilling.bind(this);
    this.setForceLogout = this.setForceLogout.bind(this);
    this.setLastFocusedName = this.setLastFocusedName.bind(this);
    this.setPaymentMethods = this.setPaymentMethods.bind(this);
    this.logger = getLogger();
    this.setUserSwitchPlaninfo = this.setUserSwitchPlaninfo.bind(this);

    this.state = {
      applicationState,
      actions: {
        userLoggedIn: this.userLoggedIn,
        userLoggedOut: this.userLoggedOut,
        setUserAccountStatus: this.setUserAccountStatus,
        setAddons: this.setAddons,
        setPlayer: this.setPlayer,
        setPlayerConfig: this.setPlayerConfig,
        setLocale: this.setLocale,
        setSubscriptions: this.setSubscriptions,
        setParentalLevel: this.setParentalLevel,
        showGlobalModal: this.showGlobalModal,
        hideGlobalModal: this.hideGlobalModal,
        showSettingsModal: this.showSettingsModal,
        hideSettingsModal: this.hideSettingsModal,
        changeTheme: this.changeTheme,
        setCurrentFocus: this.setCurrentFocus,
        setMenuItemFocus: this.setMenuItemFocus,
        setCarouselItemFocus: this.setCarouselItemFocus,
        setCarouselFocus: this.setCarouselFocus,
        setPosterLandscapeImage: this.setPosterLandscapeImage,
        getHomePath: this.getHomePath,
        applyKidsTheme: this.applyKidsTheme,
        setAppStartedValue: this.setAppStartedValue,
        setLayoutScrollTop: this.setLayoutScrollTop,
        setUserBilling: this.setUserBilling,
        setShowPromotionPageValue: this.setShowPromotionPageValue,
        setForceLogout: this.setForceLogout,
        setMediaControlItemFocus: this.setMediaControlItemFocus,
        setLastFocusedName: this.setLastFocusedName,
        setPaymentMethods: this.setPaymentMethods,
        setUserSwitchPlaninfo: this.setUserSwitchPlaninfo
      }
    };
    this.checkInitiallyRenderedGlobalModal();
    this.checkInitiallyRenderedSettingsModal();
  }

  setLastFocusedName(focusedName) {
    this.setAppState("lastFocusedName", focusedName);
  }

  setAppState(property, value, callback) {
    const newState = {
      ...this.state
    };
    newState.applicationState[property] = value;
    this.setState(newState, callback);
    applicationState = newState.applicationState;
  }

  userLoggedIn(userData) {
    this.setAppState("userLogged", true);
    this.setInitialParentalLevel(userData.settings.parentalControl);
    this.setLocale(userData.settings.language || userData.locale);
    this.setUserAccountStatus(userData.settings.accountStatus);
    this.setAddons(userData.settings.addons);
    this.setAppState("userRelatedModules", null);
  }

  userLoggedOut() {
    this.setMenuItemFocus();
    this.setAppState("userLogged", false);
    this.resetParentalLevel();
    this.setUserAccountStatus(null);
    this.setAddons([]);
    this.setAppState("userRelatedModules", null);
    this.setAppState("paymentMethods", []);
   this.setAppState("userSwitchPlaninfo", null);
  }

  setUserAccountStatus(status) {
    this.setAppState("userAccountStatus", status);
  }

  setAddons(addons) {
    this.setAppState("addons", addons);
  }

  setUserBilling(billing) {
    this.setAppState("userBilling", billing);
  }

  setUserSwitchPlaninfo(billing) {
    this.setAppState("userSwitchPlaninfo", billing);
  }
  
  setPaymentMethods(paymentMethod) {
    const paymentMethodDictionary = [...this.state.applicationState.paymentMethods, paymentMethod];
    this.setAppState("paymentMethods", paymentMethodDictionary);
  }

  async setLocale(locale) {
    this.setAppState("userLocale", locale);
    const result = await i18n.setLocale(locale);
    // if invalid token error set Force logout flag
    result && (await this.setForceLogout(result));
  }

  async setForceLogout(e) {
    const forceLogoutStatus = await isForceLogout(e);
    if (forceLogoutStatus === true) {
      this.setAppState("forceLogout", true);
    }
  }

  setSubscriptions(subscriptions) {
    this.setAppState("subscriptions", subscriptions);
  }

  setPlayer(player) {
    this.setAppState("player", player);
  }

  setPlayerConfig(config) {
    this.setAppState("playerConfig", config);
  }

  setCurrentFocus(focusPath) {
    // FIXME: Performance issue, pending refactor.
    // this.setAppState("currentFocus", focusPath);
    applicationState.currentFocus = focusPath;
  }

  setInitialParentalLevel(level) {
    getSdk().setInitialParentalLevel(level);
    this.setAppState("parentalLevel", level);
  }

  async setParentalLevel(level, password) {
    try {
      await getSdk().changeParentalLevel(level, password);
      this.setAppState("parentalLevel", level);
    } catch (e) {
      await this.setForceLogout(e);
      throw e;
    }
  }

  resetParentalLevel() {
    const parentalList = config.get().parentalControlList;
    getSdk().setInitialParentalLevel();
    this.setAppState("parentalLevel", parentalList[parentalList.length - 1]);
  }

  checkInitiallyRenderedGlobalModal() {
    this.state.applicationState.globalModal && this.handleGlobalModalConfig(this.state.applicationState.globalModal);
  }

  checkInitiallyRenderedSettingsModal() {
    this.state.applicationState.settingsModal &&
      this.handleSettingsModalConfig(this.state.applicationState.settingsModal);
  }

  setMenuItemFocus(index = 0) {
    this.setAppState("focusedMainMenuItemIndex", index);
  }
  setMediaControlItemFocus(index = 0) {
    this.setAppState("focusedMediaControlItemIndex", index);
  }

  setCarouselItemFocus(index = 0) {
    // FIXME: Performance issue, pending refactor.
    // this.setAppState("focusedCarouselItemIndex", index);
    applicationState.focusedCarouselItemIndex = index;
  }

  setLayoutScrollTop(scrollTop = 0) {
    // FIXME: Performance issue, pending refactor.
    // this.setAppState("layoutScrollTop", index);
    applicationState.layoutScrollTop = scrollTop;
  }

  setCarouselFocus(index = 0) {
    // FIXME: Performance issue, pending refactor.
    // this.setAppState("focusedCarouselIndex", index);
    applicationState.focusedCarouselIndex = index;
  }

  setPosterLandscapeImage(url) {
    applicationState.posterLandscapeImageForBuyAsset = url;
  }

  getHomePath(isDeepLink = false, isSeries) {
    // FIXME: Handle generically the parent threshold instead of using the name "kids".
    return applicationState.parentalLevel === "1"
      ? routes.kids
      : isDeepLink
      ? !!isSeries
        ? routes.series
        : routes.movies
      : routes.home;
  }

  applyKidsTheme() {
    getAppState().parentalLevel === "1" && this.changeTheme("kids");
  }

  setAppStartedValue() {
    this.setAppState("appStarted", true);
  }

  setShowPromotionPageValue(value = false) {
    this.setAppState("showPromotionPage", value);
  }

  handleGlobalModalConfig(config) {
    if (config && config.dismissable !== false) {
      config.dismiss = this.hideGlobalModal;
    }
    if (config.onShowListener) {
      config.onShowListener();
    }
  }

  handleSettingsModalConfig(config) {
    if (config && config.dismissable !== false) {
      config.dismiss = this.hideSettingsModal;
    }
    if (config.onShowListener) {
      config.onShowListener();
    }
  }

  showGlobalModal(config) {
    this.handleGlobalModalConfig(config);
    this.setAppState("globalModal", config);
  }

  showSettingsModal(config) {
    this.handleSettingsModalConfig(config);
    this.setAppState("settingsModal", config);
  }

  hideGlobalModal(callback) {
    const { globalModal: config } = applicationState;
    if (config && config.onHideListener) {
      config.onHideListener();
    }
    this.setAppState("globalModal", null);
    callback && callback();
    // Returns true to match de global onBack behaviour (App.js).
    return true;
  }

  hideSettingsModal(callback) {
    const { settingsModal: config } = applicationState;
    if (config && config.onHideListener) {
      config.onHideListener();
    }
    this.setAppState("settingsModal", null);
    callback && callback();
    // Returns true to match de global onBack behaviour (App.js).
    return true;
  }

  changeTheme(themeName) {
    //this.setAppState("theme", themes[themeName]);
  }

  shouldComponentUpdate(nextProps, nextState) {
    // FIXME: AppState complete refactor to make it re-render only when required.
    return true;
    // return (
    //   this.state.userLogged !== nextState.userLogged ||
    //   this.state.userLocale !== nextState.userLocale ||
    //   this.state.parentalLevel !== nextState.parentalLevel ||
    //   this.state.theme !== nextState.theme ||
    //   this.state.userAccountStatus !== nextState.userAccountStatus ||
    //   this.state.globalModal !== nextState.globalModal
    // );
  }

  render() {
    return <ApplicationState.Provider value={this.state}>{this.props.children}</ApplicationState.Provider>;
  }
}

/**
 * Higher order component that becomes it's wrapped components aware of the state. Those components that are wrapped by
 * withAppState will receive an extra prop called applicationState that will be an object representing the current
 * globalstate of the app.
 * @param {Component} Child The component that needs to receive the state as an extra prop.
 * @returns {Component} A react component that is the same as Child but will receive an extra prop called
 * applicationState
 */
export const withAppState = Child => {
  return class extends Component {
    constructor(props) {
      super(props);
      Child.defaultFocusPath && (this.defaultFocusPath = Child.defaultFocusPath);
      Child.name && (this.name = Child.name);
    }
    render() {
      return (
        <ApplicationState.Consumer>
          {state => <Child {...this.props} applicationState={state.applicationState} stateActions={state.actions} />}
        </ApplicationState.Consumer>
      );
    }
  };
};

export function getAppState() {
  return applicationState;
}
